import type { ReactNode } from 'react';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { VStack } from '@astryxdesign/core/Layout';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { errorMessage } from '@/lib/api/errors';

export function LoadingRows({ rows = 3, height = 72, label = 'Загрузка' }: { rows?: number; height?: number; label?: string }) {
  return (
    <VStack gap={2} as="section">
      <span className="visually-hidden" role="status">
        {label}
      </span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} index={i} height={height} radius={3} />
      ))}
    </VStack>
  );
}

export function ErrorState({
  error,
  onRetry,
  title = 'Не удалось загрузить',
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  return (
    <Banner
      status="error"
      title={title}
      description={errorMessage(error)}
      endContent={onRetry ? <Button label="Повторить" size="sm" onClick={onRetry} /> : undefined}
    />
  );
}

interface QueryStateProps<T> {
  query: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loading?: ReactNode;
  errorTitle?: string;
  children: (data: T) => ReactNode;
}

/** One place for loading / error / empty / success of a network-backed block. */
export function QueryState<T>({ query, isEmpty, empty, loading, errorTitle, children }: QueryStateProps<T>) {
  if (query.isPending) return <>{loading ?? <LoadingRows />}</>;
  if (query.isError || query.data === undefined) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} title={errorTitle} />;
  }
  if (isEmpty?.(query.data)) return <>{empty ?? <EmptyState title="Пока пусто" isCompact />}</>;
  return <>{children(query.data)}</>;
}
